import { FileCheck2 } from 'lucide-react';
import type {
  BauvorhabenVertragsunterlagen, Vertragsunterlage, VertragsunterlagenStatus,
  VertragsunterlagePatch,
} from '@/types';

const statusLabels: Record<VertragsunterlagenStatus, string> = {
  offen: 'Offen',
  eingereicht: 'Eingereicht',
  vollständig: 'Vollständig',
};

const statusClasses: Record<VertragsunterlagenStatus, string> = {
  offen: 'bg-gray-100 text-gray-800',
  eingereicht: 'bg-amber-100 text-amber-800',
  vollständig: 'bg-green-100 text-green-800',
};

/** Heutiges Datum als YYYY-MM-DD in Europe/Berlin (Wanduhr der App-Daten). */
function todayBerlinISO(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

/** YYYY-MM-DD → DD.MM.YYYY (reines Datum, keine Zeitzonen-Mathe nötig). */
function formatDateDE(iso: string | null): string {
  return iso ? iso.split('-').reverse().join('.') : '';
}

/**
 * Vertragsunterlagen-Checkliste am Bauvorhaben (A-Checkliste-MWb-Ersatz):
 * Der Vertrieb (Massive Wohnbau, extern) pflegt selbst Status +
 * Eingangsdatum je Position, statt gescannte Checklisten-PDFs per Mail
 * zu schicken, die GPS hinterher jagen muss. 'vollständig' ⇔
 * Eingangsdatum wird atomar gesendet (API-Invariante, nie sichtbar
 * gebrochen). Lesend für alle mit BV-Sicht; Schreiben approver+ oder
 * Vertrieb am eigenen BV (canEdit).
 */
export function BauvorhabenVertragsunterlagenCard({
  data, canEdit, onUpdate,
}: {
  data: BauvorhabenVertragsunterlagen | null;
  canEdit: boolean;
  onUpdate: (itemKey: string, patch: VertragsunterlagePatch) => Promise<void>;
}) {
  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileCheck2 className="h-5 w-5 text-gray-400" />
          Vertragsunterlagen
        </h2>
        <p className="text-sm text-gray-500">Checkliste konnte nicht geladen werden.</p>
      </div>
    );
  }

  // Status-Pille: „Vollständig" setzt immer ein Eingangsdatum (Default
  // heute), weg von „vollständig" räumt es ab.
  const handleStatus = (item: Vertragsunterlage, status: VertragsunterlagenStatus) => {
    const patch: VertragsunterlagePatch = { status };
    if (status === 'vollständig' && !item.received_date) {
      patch.received_date = todayBerlinISO();
    }
    if (status !== 'vollständig' && item.received_date) {
      patch.received_date = null;
    }
    onUpdate(item.item_key, patch);
  };

  // Eingangsdatum ⇔ Status vollständig atomar mitsenden.
  const handleDate = (item: Vertragsunterlage, value: string) => {
    const patch: VertragsunterlagePatch = { received_date: value || null };
    if (value) {
      patch.status = 'vollständig';
    } else if (item.status === 'vollständig') {
      patch.status = 'eingereicht';
    }
    onUpdate(item.item_key, patch);
  };

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileCheck2 className="h-5 w-5 text-gray-400" />
          Vertragsunterlagen
        </h2>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
            data.complete_count === data.total_count
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {data.complete_count}/{data.total_count} vollständig
        </span>
      </div>

      <ul className="divide-y divide-gray-100">
        {data.items.map((item) => (
          <li key={item.item_key} className="py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-gray-900">{item.label}</span>
              {canEdit ? (
                <div className="flex gap-1">
                  {(Object.keys(statusLabels) as VertragsunterlagenStatus[]).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleStatus(item, status)}
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                        item.status === status
                          ? statusClasses[status]
                          : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      {statusLabels[status]}
                    </button>
                  ))}
                </div>
              ) : (
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses[item.status]}`}>
                  {statusLabels[item.status]}
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
              <label className="inline-flex items-center gap-1.5">
                <span className="text-gray-400">Eingang</span>
                {canEdit ? (
                  <input
                    type="date"
                    value={item.received_date ?? ''}
                    onChange={(e) => handleDate(item, e.target.value)}
                    className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-800 focus:border-primary-500 focus:outline-none"
                  />
                ) : (
                  <span className={item.received_date ? 'text-gray-800' : 'text-gray-300'}>
                    {formatDateDE(item.received_date) || '—'}
                  </span>
                )}
              </label>
              {canEdit && (
                <input
                  type="text"
                  defaultValue={item.note ?? ''}
                  placeholder="Bemerkung…"
                  onBlur={(e) => {
                    const value = e.target.value.trim() || null;
                    if (value !== (item.note ?? null)) {
                      onUpdate(item.item_key, { note: value });
                    }
                  }}
                  className="min-w-[160px] flex-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
                />
              )}
              {!canEdit && item.note && (
                <span className="text-gray-500">{item.note}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}